import { ReactNode, useCallback, useEffect, useRef, useSyncExternalStore } from 'react';
import { projectStore, useWorkspaceScope as useWorkspaceScopeShared } from '@esgcaffe/ui-common';
import api, { tokenManager } from '../services/api';

/**
 * Shell-side workspace-scope bootstrap.
 *
 * Deliberately mirrors FinancialYearProvider: the shell is the only context
 * that fetches, and it pushes results into the shared @esgcaffe/ui-common
 * store, which propagates to every loaded MFE over BroadcastChannel +
 * localStorage. No MFE fetches this for itself.
 *
 * Two requests, in order:
 *   1. the workspace scope (super-admin's setting, read via identity-config)
 *   2. the project list, but ONLY when the scope actually uses projects
 *
 * The second is skipped for a financial-year-scoped workspace, so organizations
 * that never turn this on make no extra call and see no change at all.
 */

interface WorkspaceScopeProviderProps {
  children: ReactNode;
}

export const WorkspaceScopeProvider = ({ children }: WorkspaceScopeProviderProps) => {
  const authToken = useSyncExternalStore(
    tokenManager.subscribeAuthToken,
    () => tokenManager.getToken() ?? '',
    () => '',
  );

  // Guard against duplicate fetches in StrictMode and re-renders.
  const lastFetchedTokenRef = useRef<string | null>(null);

  const bootstrap = useCallback(async () => {
    projectStore.setLoading(true);
    projectStore.setError(null);

    try {
      const scopeRes = await api.getWorkspaceScope();
      // setScope also clears any stale project selection when the dimension
      // is off, so an organization moved back to FY stops sending a project
      // header it no longer has a picker for.
      projectStore.setScope(scopeRes?.data?.scope);

      if (projectStore.getState().scope === 'fy') {
        projectStore.setProjects([]);
        return;
      }

      const projectsRes = await api.getWorkspaceProjects();
      projectStore.setProjects(projectsRes ?? []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load workspace scope';
      console.error('Error loading workspace scope:', err);
      // Leave the scope at its last known value rather than forcing one: a
      // transient failure should not make a picker vanish mid-session.
      projectStore.setError(message);
    } finally {
      projectStore.setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authToken) {
      lastFetchedTokenRef.current = null;
      projectStore.reset();
      return;
    }
    if (lastFetchedTokenRef.current === authToken) return;
    lastFetchedTokenRef.current = authToken;
    void bootstrap();
  }, [authToken, bootstrap]);

  return <>{children}</>;
};

export const useWorkspaceScope = useWorkspaceScopeShared;

export default WorkspaceScopeProvider;
